import type { SelectItem, ServerSearch } from "@synthesis/shared-ui";

import type { RoleFneSearchResult } from "../../../shared/hooks/use-role-fne-search";
import { createRoleFneServerSearch, ROLE_FNE_DEFAULT_PAGE_SIZE } from "../../../shared/hooks/use-role-fne-search";
import { GLOBAL_ROLE, GLOBAL_ROLES } from "../../../shared/hooks/use-user-roles";
import type { ApplicationUiAction } from "./application-detail-controls.lib";

// ------------------------------ TYPES ------------------------------

/** Действия назначения/переназначения участников процесса через универсальное модальное окно выбора сотрудника. */
export type AssignEmployeeAction = Extract<
  ApplicationUiAction,
  "intake-pass" | "redirect-ng" | "send-to-ntp" | "redirect-ntp" | "assign-ntp-resp" | "reassign-ntp-responsible"
>;

type SnackMessages = { pending: string; fulfilled: string; rejected: string };

// ------------------------------ CONSTANTS ------------------------------

/** Кандидаты на назначение ограничиваются глобальными ролями Keycloak, релевантными действию. */
export const EMPLOYEE_ACTION_ROLES: Record<AssignEmployeeAction, string[]> = {
  "intake-pass": [GLOBAL_ROLE.INTERNAL_APPARATUS_NG],
  "redirect-ng": [GLOBAL_ROLE.INTERNAL_APPARATUS_NG],
  "send-to-ntp": [GLOBAL_ROLE.INTERNAL_NTP_CHIEF],
  "redirect-ntp": [GLOBAL_ROLE.INTERNAL_NTP_CHIEF],
  "assign-ntp-resp": [GLOBAL_ROLE.INTERNAL_NTP_RESPONSIBLE],
  "reassign-ntp-responsible": [GLOBAL_ROLE.INTERNAL_NTP_RESPONSIBLE],
};

export const EMPLOYEE_ACTION_SNACK_MESSAGES: Record<AssignEmployeeAction, SnackMessages> = {
  "intake-pass": {
    pending: "Направление в аппарат НГ",
    fulfilled: "Заявка направлена в аппарат НГ",
    rejected: "Не удалось направить заявку в аппарат НГ",
  },
  "redirect-ng": {
    pending: "Назначение согласующих",
    fulfilled: "Согласующие назначены",
    rejected: "Не удалось назначить согласующих",
  },
  "send-to-ntp": {
    pending: "Выполняется назначение руководителя...",
    fulfilled: "Заявка направлена в НТП",
    rejected: "Не удалось назначить руководителя\nПопробуйте еще раз",
  },
  "redirect-ntp": {
    pending: "Выполняется переназначение руководителя...",
    fulfilled: "Руководитель НТП успешно изменен",
    rejected: "Не удалось переназначить руководителя\nПопробуйте еще раз",
  },
  "assign-ntp-resp": {
    pending: "Назначение ответственного НТП",
    fulfilled: "Ответственный НТП назначен",
    rejected: "Не удалось назначить ответственного НТП",
  },
  "reassign-ntp-responsible": {
    pending: "Назначение согласующих",
    fulfilled: "Согласующие назначены",
    rejected: "Не удалось назначить согласующих",
  },
};

// ------------------------------ HELPERS ------------------------------

const readRecordField = (record: Record<string, unknown>, ...keys: string[]): string => {
  for (const key of keys) {
    const value = record[key];

    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return "—";
};

const GLOBAL_ROLE_PREFIXES = ["ВНЕШНЕЕ СОГЛАСОВАНИЕ", "ВНУТРЕННЕЕ СОГЛАСОВАНИЕ"] as const;

const stripGlobalRolePrefix = (role: string): string => {
  for (const prefix of GLOBAL_ROLE_PREFIXES) {
    if (role.startsWith(`${prefix} `)) {
      return role.slice(prefix.length + 1).trim();
    }
  }

  return role;
};

const toSentenceCase = (value: string): string => {
  const words = value.toLowerCase().split(/\s+/).filter(Boolean);

  if (words.length === 0) {
    return "";
  }

  words[0] = `${words[0]?.charAt(0).toUpperCase()}${words[0]?.slice(1)}`;

  return words
    .map((word) => {
      if (word === "нг" || word === "нтп") {
        return word.toUpperCase();
      }

      return word;
    })
    .join(" ");
};

const readUserDepartment = (user: RoleFneSearchResult): string => {
  const groupName = user.groups?.[0]?.name;

  if (groupName) {
    return groupName;
  }

  return readRecordField(user, "department", "departmentName", "unit", "подразделение");
};

const readUserPosition = (user: RoleFneSearchResult): string => {
  const roles = user.realmRoles ?? [];
  const matchedRole = roles.find((role) => role in GLOBAL_ROLES);

  if (!matchedRole) {
    return readRecordField(user, "position", "jobTitle", "title", "должность");
  }

  return toSentenceCase(stripGlobalRolePrefix(matchedRole));
};

const formatPersonNameWithPatronymic = (user: RoleFneSearchResult): string => {
  const patronymic = user.attributes?.middleName?.[0] ?? user.middleName;
  const name = [user.lastName, user.firstName, patronymic].filter(Boolean).join(" ");

  return name || "—";
};

const compareEmployees = (left: RoleFneSearchResult, right: RoleFneSearchResult): number =>
  formatPersonNameWithPatronymic(left).localeCompare(formatPersonNameWithPatronymic(right), "ru");

const toEmployeeSelectItem = (user: RoleFneSearchResult): SelectItem => ({
  value: user.id,
  label: [
    { label: formatPersonNameWithPatronymic(user), tooltip: formatPersonNameWithPatronymic(user), width: "60%" },
    { label: readUserDepartment(user), tooltip: readUserDepartment(user), width: "20%" },
    { label: readUserPosition(user), tooltip: readUserPosition(user), width: "20%" },
  ],
  data: user,
});

// ------------------------------ SERVER SEARCH ------------------------------

/**
 * Удалённый поиск сотрудников по ролям действия (Keycloak search-role-fne).
 * Пункты списка — ФИО / подразделение / должность, в алфавитном порядке по ФИО.
 */
export const createEmployeeServerSearch = (roles: string[], roadsIds: string[] = []): ServerSearch => {
  const base = createRoleFneServerSearch(roles, ROLE_FNE_DEFAULT_PAGE_SIZE, toEmployeeSelectItem, roadsIds);

  return {
    ...base,
    fetchItems: async (search, page, signal) => {
      const results = await base.fetchItems(search, page, signal);

      if (!Array.isArray(results) || results.length === 0) {
        return results as SelectItem[];
      }

      return (results as SelectItem[]).toSorted((a, b) => compareEmployees(a.data as RoleFneSearchResult, b.data as RoleFneSearchResult));
    },
  };
};
